// client/src/pages/School_Admin/Academics/TimetableOptionsTab.js
//
// SETUP · PAIRING — subjects that may run in the same period.
//
// Geography, Economics and Further Maths can share one period because no student
// takes more than one of them. It is not an optimisation; it is what makes a
// Sixth Form week fit. Counted as three subjects each needing their own periods,
// a week that runs every year comes back "over by 14" and the school is told to
// cut subjects it does not need to.
//
// NOTHING PAIRS UNTIL SOMEBODY SAYS SO. Marking "Core for" on the Subjects tab
// used to pair every Arts subject with every Science subject in the class, which
// produced pairings nobody recognised and no screen accounted for. The series is
// still read and still offered here as something to create with one click — but
// a rule about which subjects may share a period is too consequential to be
// inferred from a column filled in for another purpose.
//
// The table is still `option_blocks` and the API still says block — the word a
// school uses and the word the database uses are allowed to differ, and renaming
// a table that four handlers read would be a larger change than the vocabulary
// is worth.
//
// DECLARED WINS, CONTRADICTION IS SURFACED. `student_subjects` describes the
// students enrolled today, not the school's rule — a school building next
// year's timetable has no selections yet. So the school declares the block, and
// where the data disagrees this says so by name and by count, and a person
// decides.

import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
  MenuItem, Paper, Select, Stack, TextField, Typography, useTheme,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';

export default function OptionsTab({ data, busy, onSave, onDelete }) {
  const { t } = useTranslation();
  const [classId, setClassId] = useState('');
  const [draft, setDraft] = useState(null);   // {id?, name, subjectIds}

  const classes = useMemo(() => data?.classes || [], [data]);
  const blocks = useMemo(() => data?.blocks || [], [data]);

  useEffect(() => {
    if (!classes.length) return;
    setClassId((held) => (classes.some((c) => String(c.id) === String(held)) ? held : classes[0].id));
  }, [classes]);

  const mine = useMemo(
    () => blocks.filter((b) => String(b.classId) === String(classId)),
    [blocks, classId],
  );
  const available = useMemo(
    () => (data?.classSubjects || []).filter((r) => String(r.classId) === String(classId)),
    [data, classId],
  );
  const mySeries = useMemo(
    () => (data?.series || []).find((g) => String(g.classId) === String(classId)) || null,
    [data, classId],
  );
  const mySuggestions = useMemo(
    () => (data?.suggestions || []).filter((s) => String(s.classId) === String(classId)),
    [data, classId],
  );
  const myContradictions = useMemo(
    () => (data?.contradictions || []).filter((c) => String(c.classId) === String(classId)),
    [data, classId],
  );

  if (!data) return <CircularProgress size={20} />;

  const subjectName = (id) => available.find((r) => Number(r.subjectId) === Number(id))?.subjectName || `Subject ${id}`;
  const periodsOf = (id) => available.find((r) => Number(r.subjectId) === Number(id))?.periodsPerWeek ?? null;

  // What the block will actually cost: the greatest of its members, not the sum.
  const costOf = (ids) => Math.max(0, ...ids.map((id) => Number(periodsOf(id)) || 0));
  const sumOf = (ids) => ids.reduce((s, id) => s + (Number(periodsOf(id)) || 0), 0);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Select size="small" value={classId} onChange={(e) => { setClassId(e.target.value); setDraft(null); }} sx={{ minWidth: 240 }}>
          {classes.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
        </Select>
        {mySeries?.paired && (
          <Chip size="small" color="success" variant="outlined" label={t('timetableGenerator.options.paired', { defaultValue: 'Arts and Science run in parallel' })} />
        )}
        <Button
          size="small" variant="outlined" startIcon={<AddIcon />} disabled={busy || !!draft}
          onClick={() => setDraft({ name: `Pairing ${mine.length + 1}`, subjectIds: [] })}
        >
          {t('timetableGenerator.options.newPairing', { defaultValue: 'New pairing' })}
        </Button>
      </Stack>

      {/* CARRIED OVER, AND SAID SO. Settings an admin did not enter this
          year, shown with no explanation, are settings they will not trust —
          and they need to know that editing anything here makes this year's
          own copy, leaving last year's record intact. */}
      {data.carriedFrom && (
        <Alert severity="info">
          {t('timetableGenerator.carried', {
            defaultValue: 'This is last year\'s setup, shown because nothing has been entered for '
              + 'the current year yet. Change anything here and the whole set is copied into this '
              + 'year first, so last year\'s record is left exactly as it was.',
          })}
        </Alert>
      )}

      {/* WHAT THE DATA SAYS ABOUT WHAT WAS DECLARED. Never applied silently:
          the school's rule stands and the disagreement is shown. */}
      {myContradictions.map((c, i) => (
        <Alert severity="error" key={i}>{c.message}</Alert>
      ))}

      {/* THE SERIES, SHOWN RATHER THAN INFERRED SILENTLY.
          A school that filled in "Core for" on the Subjects tab has already said
          its Arts and Science students are different people. Pairing them
          without showing it would leave an admin looking for a block they never
          need to create — which is exactly what happened before this was here. */}
      {/* WHETHER THE SERIES IS ALREADY PAIRED is read off the pairings
          themselves rather than a flag on the row. A pairing created from the
          series is an ordinary pairing afterwards — editable, removable, and
          indistinguishable from one typed by hand, which is the point. */}
      <SeriesPanel
        series={mySeries}
        busy={busy}
        alreadyPaired={(() => {
          const wanted = [...(mySeries?.arts || []), ...(mySeries?.science || [])]
            .map((x) => Number(x.subjectId));
          if (wanted.length < 2) return false;
          return mine.some((b) => {
            const held = new Set((b.subjectIds || []).map(Number));
            return wanted.every((id) => held.has(id));
          });
        })()}
        onCreate={(subjectIds) => setDraft({
          name: `Arts / Science ${mine.length + 1}`,
          subjectIds,
        })}
      />

      {mySeries && !mySeries.paired && (mySeries.arts.length || mySeries.science.length) ? (
        <Alert severity="warning">
          Only the {mySeries.arts.length ? 'Arts' : 'Science'} series is marked in this class, so
          there is nothing for it to run alongside. Mark the other series on the Subjects tab —
          set "Core for" to {mySeries.arts.length ? 'Science' : 'Arts'} on the subjects that
          stream takes — and they will pair automatically.
        </Alert>
      ) : null}

      {mine.map((block) => {
        const editing = draft?.id === block.id;
        return (
          <Card key={block.id} variant="outlined">
            <CardContent>
              {editing ? (
                <BlockEditor
                  draft={draft} setDraft={setDraft} available={available} busy={busy}
                  onSave={async () => { await onSave({ ...draft, classId }); setDraft(null); }}
                />
              ) : (
                <>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle2">{block.name}</Typography>
                    <Box sx={{ flex: 1 }} />
                    <Chip
                      size="small" variant="outlined"
                      label={`costs ${costOf(block.subjectIds)} periods, not ${sumOf(block.subjectIds)}`}
                      color={sumOf(block.subjectIds) > costOf(block.subjectIds) ? 'success' : 'default'}
                    />
                    <Button size="small" disabled={busy}
                      onClick={() => setDraft({ id: block.id, name: block.name, subjectIds: block.subjectIds })}>
                      Edit
                    </Button>
                    <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} disabled={busy}
                      onClick={() => onDelete(block.id)}>
                      Remove
                    </Button>
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {block.subjectIds.map((id) => (
                      <Chip key={id} size="small" label={`${subjectName(id)}${periodsOf(id) ? ` · ${periodsOf(id)}p` : ''}`} />
                    ))}
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>
        );
      })}

      {draft && !draft.id && (
        <Card variant="outlined">
          <CardContent>
            <BlockEditor
              draft={draft} setDraft={setDraft} available={available} busy={busy}
              onSave={async () => { await onSave({ ...draft, classId }); setDraft(null); }}
            />
          </CardContent>
        </Card>
      )}

      {!mine.length && !draft && (
        <Alert severity="info">
          {classes.find((c) => String(c.id) === String(classId))?.name} has no pairing declared,
          so every subject is counted as needing its own periods. That is right for Forms 1–5
          where everyone takes everything, and wrong for a Sixth Form.
        </Alert>
      )}

      {/* SUGGESTIONS, NOT RULES. Evidence about the students enrolled today. */}
      {!!mySuggestions.length && (
        <>
          <Divider />
          <Typography variant="subtitle2">From the subjects students have actually chosen</Typography>
          <Typography variant="caption" color="text.secondary">
            No student in this class takes more than one subject in each group below, so they could
            share a period. This is what {data.choicesRecorded} recorded selections show today — it is
            a suggestion, not a rule, and next year's intake may differ.
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack spacing={1}>
              {mySuggestions.map((s, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  {s.subjectNames.map((n) => <Chip key={n} size="small" variant="outlined" label={n} />)}
                  <Box sx={{ flex: 1 }} />
                  <Button size="small" disabled={busy}
                    onClick={() => setDraft({ name: `Pairing ${mine.length + 1}`, subjectIds: s.subjectIds })}>
                    Use this
                  </Button>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </>
      )}

      {!data.choicesRecorded && (
        <Alert severity="info">
          No subject selections are recorded for these classes, so nothing can be suggested — which is
          not the same as there being nothing to suggest. Declare the blocks the school already runs.
        </Alert>
      )}
    </Stack>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * The two series of a class, and what that already pairs.
 *
 * SET ON THE SUBJECTS TAB, APPLIED HERE. "Core for: Arts" and "Core for:
 * Science" on the same class is a complete statement that those are two
 * different student groups — every Arts subject may run against every Science
 * one, and two subjects of the SAME series may never. There is nothing further
 * to declare, and this says so rather than leaving an admin hunting for a
 * pairing control that should not exist.
 */
function SeriesPanel({ series, busy, onCreate, alreadyPaired }) {
  const { t } = useTranslation();
  const theme = useTheme();
  if (!series || (!series.arts.length && !series.science.length)) {
    return (
      <Alert severity="info">
        {t('timetableGenerator.options.noSeries', { defaultValue: 'No series is marked in this class. If it runs Arts and Science side by side, set "Core for" on the Subjects tab — Arts on the subjects the arts students take, Science on the others — and this tab will offer to pair them.' })}
      </Alert>
    );
  }

  const column = (label, list, tint) => (
    <Box sx={{ flex: 1, minWidth: 200 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
        {label}
        {!!list.length && (
          <Typography component="span" variant="caption" color="text.secondary">
            {' · '}{list.reduce((n, x) => n + (x.periodsPerWeek || 0), 0)} periods
          </Typography>
        )}
      </Typography>
      {list.length ? (
        <Stack spacing={0.5}>
          {list.map((x) => (
            <Chip
              key={x.subjectId} size="small" variant="outlined"
              sx={{ justifyContent: 'flex-start', bgcolor: tint }}
              label={`${x.subjectName}${x.periodsPerWeek ? ` · ${x.periodsPerWeek}p` : ''}`}
            />
          ))}
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {t('timetableGenerator.options.nothingMarked', { defaultValue: 'nothing marked' })}
        </Typography>
      )}
    </Box>
  );

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="subtitle2" gutterBottom>
        {t('timetableGenerator.options.series', { defaultValue: 'Series' })}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {t('timetableGenerator.options.seriesHint', { defaultValue: 'Set with "Core for" on the Subjects tab. Every subject on one side could run at the same time as every subject on the other — they are different students. Two subjects on the SAME side never share a period. Marking a series does not pair anything on its own; use the button below and it becomes an ordinary pairing you can edit or remove.' })}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {column('Arts', series.arts, theme.palette.action.hover)}
        <Divider orientation="vertical" flexItem />
        {column('Science', series.science, theme.palette.action.hover)}
      </Stack>
      {/* OFFERED, NOT APPLIED. One click turns the series into an ordinary
          pairing — which then shows in the list above, can be edited, and can be
          removed. Before this, the pairing existed only as an inference nobody
          could see, edit or undo. */}
      {series.paired && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          <Typography variant="caption" color="text.secondary">
            Paired, this class would spend the greater of the two — {Math.max(
              series.arts.reduce((n, x) => n + (x.periodsPerWeek || 0), 0),
              series.science.reduce((n, x) => n + (x.periodsPerWeek || 0), 0),
            )} periods — instead of their sum.
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small" variant="outlined" startIcon={<AddIcon />}
            disabled={busy || alreadyPaired}
            onClick={() => onCreate([...series.arts, ...series.science].map((x) => x.subjectId))}
          >
            {alreadyPaired
              ? t('timetableGenerator.options.seriesDone', { defaultValue: 'Already paired' })
              : t('timetableGenerator.options.pairSeries', { defaultValue: 'Pair these two series' })}
          </Button>
        </Stack>
      )}
    </Paper>
  );
}

/* -------------------------------------------------------------------------- */

function BlockEditor({ draft, setDraft, available, busy, onSave }) {
  const { t } = useTranslation();
  const chosen = draft.subjectIds || [];
  const enough = chosen.length >= 2 && String(draft.name || '').trim();

  return (
    <Stack spacing={2}>
      <TextField
        size="small" label="Name" sx={{ maxWidth: 280 }}
        placeholder="U6 Block 2"
        value={draft.name || ''}
        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
      />
      <Box>
        <Typography variant="caption" color="text.secondary">
          {t('timetableGenerator.options.pickHint', { defaultValue: 'Subjects that share a period. Click to add or remove.' })}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          {available.map((row) => {
            const on = chosen.includes(Number(row.subjectId));
            return (
              <Chip
                key={row.subjectId}
                size="small"
                label={`${row.subjectName}${row.periodsPerWeek ? ` · ${row.periodsPerWeek}p` : ''}`}
                color={on ? 'primary' : 'default'}
                variant={on ? 'filled' : 'outlined'}
                onClick={() => setDraft({
                  ...draft,
                  subjectIds: on
                    ? chosen.filter((id) => id !== Number(row.subjectId))
                    : [...chosen, Number(row.subjectId)],
                })}
              />
            );
          })}
        </Stack>
      </Box>
      <Stack direction="row" spacing={1}>
        <Button variant="contained" size="small" disabled={!enough || busy} onClick={onSave}>
          {t('timetableGenerator.options.savePairing', { defaultValue: 'Save pairing' })}
        </Button>
        <Button size="small" onClick={() => setDraft(null)}>
          {t('common.cancel', { defaultValue: 'Cancel' })}
        </Button>
        {chosen.length === 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {t('timetableGenerator.options.needTwo', { defaultValue: 'A pairing needs at least two subjects.' })}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}
